
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageSettings } from '@/api/publicClient';

const LocalizationContext = createContext();

export const useLocalization = () => useContext(LocalizationContext);

export const LocalizationProvider = ({ children }) => {
  const { t, i18n, ready } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const hasRunEffectRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('[LocalizationProvider] Starting language detection...');
      console.log('[LocalizationProvider] Current i18n language:', i18n.language);
      console.log('[LocalizationProvider] i18n ready state:', i18n.isInitialized);
      
      // Сначала пытаемся загрузить настройки языка с сервера
      try {
        const languageSettings = await getLanguageSettings();
        console.log('[LocalizationProvider] Server language settings:', languageSettings);
        
        if (languageSettings.language && languageSettings.language !== i18n.language) {
          console.log('[LocalizationProvider] Changing language from', i18n.language, 'to', languageSettings.language);
          await i18n.changeLanguage(languageSettings.language);
          // Сохраняем серверный язык в localStorage
          localStorage.setItem('i18nextLng', languageSettings.language);
          console.log('[LocalizationProvider] Language changed successfully to:', i18n.language);
        } else {
          console.log('[LocalizationProvider] Language already set to:', i18n.language);
        }
      } catch (publicError) {
        console.debug('Could not load language settings from public API:', publicError);
        // Если сервер недоступен, используем язык из localStorage
        const savedLanguage = localStorage.getItem('i18nextLng');
        console.log('[LocalizationProvider] Using saved language from localStorage:', savedLanguage);
        if (savedLanguage && savedLanguage !== i18n.language) {
          await i18n.changeLanguage(savedLanguage);
        }
      }
      
    } catch (error) {
      console.error('Error in fetchSettings:', error);
      // В случае любой ошибки используем язык из localStorage
      const savedLanguage = localStorage.getItem('i18nextLng');
      if (savedLanguage && savedLanguage !== i18n.language) {
        await i18n.changeLanguage(savedLanguage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    if (!(ready && i18n.isInitialized)) {
      console.log('[LocalizationProvider] Waiting for i18n to be ready...', { ready, isInitialized: i18n.isInitialized });
      return;
    }

    // Prevent duplicate fetch/init in React 18 StrictMode (development double-invocation per mount)
    if (import.meta.env?.DEV && hasRunEffectRef.current) {
      return;
    }
    hasRunEffectRef.current = true;

    console.log('[LocalizationProvider] i18n is ready and initialized, fetching settings...');
    let timeout;
    fetchSettings().catch(error => {
      console.error('Failed to fetch settings:', error);
      setIsLoading(false);
    });

    // Добавляем таймаут на 5 секунд
    timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('LocalizationProvider timeout - forcing ready state');
        setHasTimedOut(true);
        setIsLoading(false);
      }
    }, 5000);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [ready, i18n.isInitialized, fetchSettings]);

  const changeLanguage = useCallback(async (language) => {
    try {
      await i18n.changeLanguage(language);
      
      // Сохраняем в localStorage для всех пользователей
      localStorage.setItem('i18nextLng', language);
      
      // Обновление настроек в базе данных доступно только администраторам
      // Обычные пользователи могут менять язык только локально
      console.debug('Language changed to:', language, '- saved to localStorage');
      
    } catch (error) {
      console.error('Error changing language:', error);
    }
  }, [i18n]);

  // Определяем методы оплаты
  const paymentMethods = [
    { key: 'cash', label: t('payments.methods.cash') },
    { key: 'card', label: t('payments.methods.card') },
    { key: 'bank_transfer', label: t('payments.methods.bank_transfer') },
    { key: 'online', label: t('payments.methods.online') },
    { key: 'tbank', label: t('payments.providers.tbank') },
    { key: 'paypal', label: t('payments.providers.paypal') }
  ];

  // Определяем символ валюты
  const currencySymbol = settings?.currency_symbol || '₽';

  const value = {
    t,
    i18n,
    ready: ready || false,
    isLoading,
    settings,
    changeLanguage,
    currentLanguage: i18n.language || 'en',
    paymentMethods,
    currencySymbol,
  };

  // Добавляем таймаут для предотвращения бесконечной загрузки
  if ((!ready || isLoading) && !hasTimedOut) {
    return (
      <LocalizationContext.Provider value={value}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </LocalizationContext.Provider>
    );
  }

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
